import { Link } from 'react-router-dom';
import {
  Bell,
  BellRing,
  ChevronRight,
  Clock,
  Map as MapIcon,
  PieChart,
  Route,
  Pause,
} from 'lucide-react';
import { useEventsStore } from '@/stores/events';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface Item {
  to: string;
  label: string;
  description: string;
  icon: typeof Bell;
  iconBg: string;
  iconColor: string;
  enabled?: boolean;
}

const items: Item[] = [
  {
    to: '/notifications',
    label: 'Notificações',
    description: 'Feed em tempo real dos eventos recebidos',
    icon: BellRing,
    iconBg: 'bg-status-blocked/10',
    iconColor: 'text-status-blocked',
    enabled: true,
  },
  {
    to: '/reports/events',
    label: 'Eventos',
    description: 'Lista de eventos por período (histórico do servidor)',
    icon: Bell,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    enabled: true,
  },
  {
    to: '/reports/hours',
    label: 'Horas de Trabalho',
    description: 'Tempo total trabalhado por máquina',
    icon: Clock,
    iconBg: 'bg-status-running/10',
    iconColor: 'text-status-running',
    enabled: true,
  },
  {
    to: '/reports/route',
    label: 'Rota da máquina',
    description: 'Histórico de posições no período',
    icon: MapIcon,
    iconBg: 'bg-status-ready/10',
    iconColor: 'text-status-ready',
    enabled: true,
  },
  {
    to: '/reports/summary',
    label: 'Sumário',
    description: 'Estatísticas consolidadas',
    icon: PieChart,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    enabled: true,
  },
  {
    to: '/reports/trips',
    label: 'Viagens',
    description: 'Trajetos detalhados',
    icon: Route,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    enabled: true,
  },
  {
    to: '/reports/stops',
    label: 'Paradas',
    description: 'Tempo parado por local',
    icon: Pause,
    iconBg: 'bg-status-off/15',
    iconColor: 'text-status-off',
    enabled: true,
  },
];

export function ReportsPage() {
  const unreadCount = useEventsStore((s) => s.items.filter((e) => !e.read).length);
  return (
    <div className="mx-auto max-w-2xl space-y-3 p-4 sm:p-6">
      <Card>
        <CardContent className="p-0">
          <ul className="divide-y divide-border">
            {items.map((item) => (
              <Row
                key={item.to}
                item={item}
                badge={item.to === '/notifications' ? unreadCount : undefined}
              />
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ item, badge }: { item: Item; badge?: number }) {
  const content = (
    <>
      <span
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          item.iconBg,
          item.iconColor,
        )}
      >
        <item.icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{item.label}</p>
        <p className="truncate text-xs text-muted-foreground">
          {item.description}
          {!item.enabled && ' · em breve'}
        </p>
      </div>
      {badge && badge > 0 ? (
        <span className="rounded-full bg-status-blocked px-1.5 text-[10px] font-bold text-destructive-foreground">
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </>
  );

  const baseClass = cn(
    'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
    item.enabled ? 'hover:bg-accent' : 'cursor-not-allowed opacity-50',
  );

  if (!item.enabled) {
    return (
      <li>
        <div className={baseClass}>{content}</div>
      </li>
    );
  }
  return (
    <li>
      <Link to={item.to} className={baseClass}>
        {content}
      </Link>
    </li>
  );
}
